import axios from 'axios';
import apiClient from '../client';
import { ApiUrls } from '../ApiUrls';
import type {
    LoginRequest,
    RegisterRequest,
    SocialLoginRequest,
    AuthResponse,
    ApiError,
    LoginRegisterApiResponse,
    SocialLoginApiResponse,
    RefreshTokenResponse,
} from '../../types/auth.types';
import { isWebBuild } from '../../utils/environment';

class AuthService {
    /**
     * Login with email and password.
     * API returns { status, message, user: { access_token, refresh_token, id, email, name, provider } }.
     */
    async login(credentials: LoginRequest): Promise<AuthResponse> {
        try {
            const response = await apiClient.post<LoginRegisterApiResponse>(ApiUrls.login, credentials);
            const data = response.data;
            if (!data?.user?.access_token) {
                throw { response: { data: { message: data?.message || 'Invalid login response' } } };
            }
            return {
                access_token: data.user.access_token,
                refresh_token: data.user.refresh_token,
                user: { id: data.user.id, email: data.user.email, name: data.user.name, provider: data.user.provider },
                message: data.message,
            };
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Register a new user.
     * API returns { status, message, user: { access_token, refresh_token, id, email, name, provider } }.
     */
    async register(userData: RegisterRequest): Promise<AuthResponse> {
        try {
            const response = await apiClient.post<LoginRegisterApiResponse>(ApiUrls.register, userData);
            const data = response.data;
            if (!data?.user?.access_token) {
                throw { response: { data: { message: data?.message || 'Invalid register response' } } };
            }
            return {
                access_token: data.user.access_token,
                refresh_token: data.user.refresh_token,
                user: { id: data.user.id, email: data.user.email, name: data.user.name, provider: data.user.provider },
                message: data.message,
            };
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Social login (Google, etc.).
     * API returns { access_token, refresh_token, id, email, name, provider } at root.
     */
    async socialLogin(socialData: SocialLoginRequest): Promise<AuthResponse> {
        try {
            const response = await apiClient.post<SocialLoginApiResponse>(ApiUrls.socialLogin, socialData);
            const data = response.data;
            if (!data?.access_token) {
                throw { response: { data: { message: 'Invalid social login response' } } };
            }
            return {
                access_token: data.access_token,
                refresh_token: data.refresh_token,
                user: { id: data.id, email: data.email, name: data.name, provider: data.provider },
            };
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Refresh access token using refresh_token.
     * Uses raw axios to avoid request interceptor (no Bearer on this call).
     * Body: { refresh_token }. Returns { access_token, refresh_token }.
     */
    async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
        const { data } = await axios.post<RefreshTokenResponse>(ApiUrls.refreshToken, { refresh_token: refreshToken }, {
            headers: { 'Content-Type': 'application/json' },
            // 30s timeout — generous enough for slow connections / server cold starts
            // to avoid transient timeouts that would previously force a logout
            timeout: 30000,
        });
        if (!data?.access_token) throw new Error('Invalid refresh response');
        return data;
    }

    /**
     * Generate pairing code for plugin authentication
     */
    async generatePairingCode(): Promise<any> {
        try {
            const response = await apiClient.post(ApiUrls.generatePairingCode, {});
            return response;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Exchange pairing code for access token
     */
    async exchangePairingCode(pairingKey: string): Promise<any> {
        try {
            const response = await apiClient.post(ApiUrls.pairingCode, {
                pairing_key: pairingKey
            });
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Send access token to pairing key (for plugin authentication)
     */
    async sendAccessTokenToPairingKey(pairingKey: string, accessToken: string): Promise<any> {
        try {
            const response = await apiClient.post(ApiUrls.pairingCode, {
                pairing_key: pairingKey,
                access_token: accessToken
            });
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Pair logged-in user with pairing key (called on redirect page load)
     * Links the authenticated user session to the plugin pairing key
     */
    async pairLoggedUser(pairingKey: string): Promise<any> {
        try {
            const response = await apiClient.post(ApiUrls.pairLoggedUser, {
                pairing_key: pairingKey
            });
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get user profile
     */
    async getUserProfile(isPlugin: boolean = false): Promise<any> {
        try {
            const url = isPlugin ? `${ApiUrls.userProfile}?isPlugin=true` : ApiUrls.userProfile;
            const response = await apiClient.get(url);
            return response.data?.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Logout user
     */
    async logout(): Promise<void> {
        try {
            await apiClient.post(ApiUrls.logoutUser, {});
        } catch (error: any) {
            // Don't throw error on logout failure - still clear local state
            console.warn('Logout API call failed:', error);
        }
    }

    /**
     * Request password reset email
     */
    async forgotPassword(email: string): Promise<any> {
        try {
            const response = await apiClient.post(ApiUrls.forgotPassword, { email });
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Reset password with token
     */
    async resetPassword(token: string, newPassword: string): Promise<any> {
        try {
            const response = await apiClient.post(ApiUrls.resetPassword, {
                token,
                newPassword
            });
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Request upgrade (add points/credits)
     */
    async requestUpgrade(): Promise<any> {
        try {
            const response = await apiClient.post(ApiUrls.requestUpgrade, {});
            return response.data?.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Update user profile data
     */
    async updateUserData(userData: any): Promise<any> {
        try {
            const response = await apiClient.post(ApiUrls.saveUserData, userData);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Add user action for tracking
     */
    async addUserAction(actionData: any): Promise<any> {
        try {
            const response = await apiClient.post(ApiUrls.addUserAction, actionData);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Clean user actions
     */
    async cleanUserActions(): Promise<any> {
        try {
            const response = await apiClient.post(ApiUrls.cleanUserActions, {});
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Check if user is a new visitor
     */
    async checkVisitor(): Promise<{ isNew: boolean; wallets?: { [key: string]: number }[] }> {
        try {
            const response = await apiClient.get(ApiUrls.checkVisitor);
            return response.data || { isNew: true };
        } catch (error: any) {
            // Return default values if API fails
            console.error('Failed to check visitor:', error);
            return { isNew: true };
        }
    }

    /**
     * Update onboarding shown flag
     */
    async updateOnboardingShown(): Promise<any> {
        try {
            const response = await apiClient.post(ApiUrls.updateOnboardingShown, {});
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Update grace period notice shown flag
     * Called when user dismisses or acknowledges the grace period modal
     */
    async updateGracePeriodNoticeShown(): Promise<any> {
        try {
            const response = await apiClient.put(ApiUrls.updateGracePeriodNoticeShown, {
                gracePeriodNoticeShown: true
            });
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Handle API errors consistently
     */
    private handleError(error: any): ApiError {
        if (error.response) {
            return {
                message: error.response.data?.message || 'Request failed',
                errors: error.response.data?.errors,
                status: error.response.status,
            };
        }

        // Network error
        return {
            message: 'Network error. Please check your connection.',
            status: 0,
        };
    }
}

export const authService = new AuthService();
export default authService;

